import { useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import Stack from '@mui/material/Stack';
import { useCheckoutFlow } from '@components/Checkout/hooks/useCheckoutFlow';
import { useTermsPdfPreload } from '@hooks/useTermsPdfPreload';
import { getTermsPdfUrl } from '@services/termsPdfService';
import { useTenantConfigContext } from '@providers/TenantConfigProvider';
import { useProductOfferings } from '@hooks/useProductOfferings/useProductOfferings';
import { useSessionContext } from '@contexts/SessionContext';
import { useQualificationFlowStore } from '@store/qualificationStore/qualificationStore';
import type { ServiceQualificationAddress } from '@t/serviceQualification.types';
import { formatStructuredAddress } from '@utils/formatStructuredAddress';
import { CheckoutContainer, CheckoutPageWrapper, SummaryColumn } from '@components/Checkout/Checkout.styles';
import { CheckoutStepRenderer } from '@components/Checkout/components/CheckoutStepRenderer';
import { PlaceOrderSection } from '@components/Checkout/components/PlaceOrderSection';
import { CheckoutHeader } from '@components/Checkout/layout/CheckoutHeader';
import { OrderSummary } from '@components/Checkout/layout/OrderSummary';
import LeadCaptureDialog from '@components/modals/LeadCaptureDialog/LeadCaptureDialog';

const SERVICE_ADDRESS_FALLBACK = 'Service address not set';

type CapturedAddress =
  | (Partial<ServiceQualificationAddress> & { formattedAddress?: string; displayAddress?: string })
  | null;

const resolveAddress = (address: CapturedAddress): string => {
  if (!address) return SERVICE_ADDRESS_FALLBACK;
  if (address.displayAddress) return address.displayAddress;
  if (address.formattedAddress) return address.formattedAddress;
  // Defensive last-resort: structured fields carry the short-form country code,
  // which is not user-facing. Drop it in the rebuild so ServiceAddressDisplay
  // never renders ", CA" instead of ", Canada".
  return formatStructuredAddress({ ...address, country: undefined });
};

const CheckoutPage = () => {
  const { resolvedAddressId, capturedAddress, accessRestrictions, setQualificationFlow } = useQualificationFlowStore(
    useShallow((s) => ({
      resolvedAddressId: s.resolvedAddressId,
      capturedAddress: s.capturedAddress,
      accessRestrictions: s.qualificationResult?.accessRestrictions,
      setQualificationFlow: s.setQualificationFlow,
    }))
  );
  const serviceAddress = resolveAddress(capturedAddress);

  const flow = useCheckoutFlow({ resolvedAddressId });

  const { sessionId } = useSessionContext();
  const { plansByCategory } = useProductOfferings(sessionId ?? '', resolvedAddressId ?? '');

  const { tmfEnabled, isLoading: isTenantConfigLoading } = useTenantConfigContext();
  // Wait for the tenant config so TMF tenants never fetch the terms PDF.
  useTermsPdfPreload(!isTenantConfigLoading, getTermsPdfUrl(tmfEnabled));

  const [showLeadCapture, setShowLeadCapture] = useState(false);

  const handleEditServiceAddress = async (): Promise<void> => {
    setQualificationFlow((prev) => ({ ...prev, isChangeAddressOpen: true }));
    await flow.actions.clearCart();
  };

  return (
    <CheckoutPageWrapper>
      <CheckoutHeader onBackToCart={flow.header.onBackToCart} />

      <CheckoutContainer>
        <Stack spacing={2.5} useFlexGap sx={{ flex: 1, minWidth: 0 }}>
          <CheckoutStepRenderer
            steps={flow.steps}
            stepStatuses={flow.stepStatuses}
            activeStep={flow.activeStep}
            stepRefs={flow.stepRefs}
            onEditStep={flow.onEditStep}
            customerStepProps={{
              ...flow.customerStepProps,
              serviceAddress,
              onEditServiceAddress: handleEditServiceAddress,
            }}
            installationStepProps={{ ...flow.installationStepProps, accessRestrictions }}
            phoneStepProps={flow.phoneStepProps}
            reviewStepProps={flow.reviewStepProps}
          />

          <PlaceOrderSection {...flow.placeOrder} onOpenLeadCapture={() => setShowLeadCapture(true)} />
        </Stack>

        <SummaryColumn>
          <OrderSummary cart={flow.summary.cart} plansByCategory={plansByCategory} />
        </SummaryColumn>
      </CheckoutContainer>

      <LeadCaptureDialog
        open={showLeadCapture}
        onClose={() => setShowLeadCapture(false)}
        onSuccess={() => setShowLeadCapture(false)}
      />
    </CheckoutPageWrapper>
  );
};

export default CheckoutPage;
